import { RuleSection } from '../types';
import { upgradeMistsChapters } from './perfect-quality-3';

/**
 * Mgła nad Carcassonne — pełne zasady.
 * Tryb A: samodzielna kooperacja (poziomy 1–6).
 * Tryb B: Duchy, Zamki i Cmentarze jako dodatek do klasycznego Carcassonne.
 * requiredMistsPlayMode filtruje treść wg configuration.mistsPlayMode.
 */
const mistsSectionsRaw: RuleSection[] = [
  // ——— Mode A: Coop overview ———
  {
    id: 'mists-overview',
    title: 'Mgła — Charakter i cel gry (kooperacja)',
    requiredModuleIds: ['mists-over-carcassonne'],
    requiredMistsPlayMode: 'coop',
    editionTag: 'C3',
    sourceIds: ['mists-official', 'mists-wica'],
    keywords: ['mgla', 'kooperacja', 'duchy', 'cel'],
    level: 'basic',
    hubGroup: 'Mgła — kooperacja',
    content: [
      {
        type: 'paragraph',
        text: 'Mgła nad Carcassonne to kooperacyjna odmiana Carcassonne. Gracie wspólnie przeciwko grze: dokładacie kafelki, wystawiacie strażników (meeple), punktujecie drogi i miasta oraz uspokajacie duchy pojawiające się w mgle.',
      },
      {
        type: 'paragraph',
        text: 'Wygrywacie razem, gdy wspólny znacznik punktacji dotrze do żetonu celu (wymagana liczba punktów zależy od poziomu). Przegrywacie natychmiast, gdy musicie wystawić ducha, a w zapasie ich brakuje, albo gdy nie możecie dobrać kafelka, a jeszcze nie osiągnęliście celu.',
      },
      {
        type: 'warning',
        text: 'Nie ma farmerów: nie wolno stawiać meepla na polu (z mgłą ani bez). Nie ma punktowania końcowego pól — partia kończy się zwycięstwem albo przegraną bez finałowego rozliczenia łąk.',
      },
      {
        type: 'note',
        text: 'Pokazujcie sobie dobrane kafelki i decydujcie jako drużyna. Na wyższych poziomach współpraca jest konieczna.',
      },
    ],
  },
  {
    id: 'mists-components',
    title: 'Mgła — Elementy zestawu',
    requiredModuleIds: ['mists-over-carcassonne'],
    requiredMistsPlayMode: 'coop',
    editionTag: 'C3',
    sourceIds: ['mists-official'],
    keywords: ['komponenty', 'duchy', 'psy', 'cmentarz', 'zamek'],
    level: 'basic',
    hubGroup: 'Mgła — kooperacja',
    content: [
      {
        type: 'bulletList',
        items: [
          '60 kafelków krajobrazu (drogi, miasta, zamki, cmentarze; większość z mgłą i symbolami duchów)',
          '1 kafelek startowy (liczy się jak 4 zwykłe kafelki; ma symbole duchów)',
          '30 meepli strażników w 6 kolorach (po 5): czerwony, zielony, niebieski, żółty, czarny, różowy',
          '15 duchów (figurki do zapasu)',
          'Tor punktacji',
          'Żeton celu',
          '2 żetony psów (Rufus = 1, Ronja = 2) — używane od poziomu 3',
          'Karta podsumowania poziomów',
        ],
      },
    ],
  },
  {
    id: 'mists-setup-l1',
    title: 'Mgła — Przygotowanie poziomu 1',
    requiredModuleIds: ['mists-over-carcassonne'],
    requiredMistsPlayMode: 'coop',
    editionTag: 'C3',
    sourceIds: ['mists-official'],
    keywords: ['przygotowanie', 'poziom 1'],
    level: 'basic',
    hubGroup: 'Mgła — kooperacja',
    content: [
      {
        type: 'numberedList',
        items: [
          'Połóż kafelek startowy na środku stołu zgodnie z ilustracją w zestawie.',
          'Odłóż do pudełka wszystkie 5 cmentarzy i wszystkie 5 zamków.',
          'Potasuj pozostałe kafelki i ułóż zakryty stos w zasięgu wszystkich.',
          'Połóż tor punktacji i kartę podsumowania poziomów obok.',
          'Połóż żeton celu na polu 0/50 toru (wymagane 50 punktów).',
          'Odłóż oba psy do pudełka.',
          'Każdy gracz bierze 5 meepli swojego koloru do zapasu.',
          'Jeden meeple w niewybranym kolorze połóż na 0/50 jako wspólny znacznik punktacji.',
          'Pozostałe meeple odłóż do pudełka.',
          '15 duchów połóż jako zapas obok toru; 3 z nich ustaw na symbolach duchów kafelka startowego.',
        ],
      },
      {
        type: 'heading',
        text: '2 graczy i solo',
        level: 3,
      },
      {
        type: 'bulletList',
        items: [
          '2 graczy: każdy ma 6 meepli w 2 kolorach (po 3); wspólny znacznik punktacji w trzecim kolorze.',
          'Solo: 12 meepli w 4 kolorach (po 3); wspólny znacznik w piątym kolorze.',
          'W turze możesz wystawić meepla w dowolnym ze swoich kolorów.',
        ],
      },
      {
        type: 'exception',
        text: 'Jeśli w dowolnym momencie w zapasie jest mniej duchów niż musisz wystawić — natychmiast przegrywacie.',
      },
    ],
  },
  {
    id: 'mists-turn-order',
    title: 'Mgła — Przebieg tury (poziom 1)',
    requiredModuleIds: ['mists-over-carcassonne'],
    requiredMistsPlayMode: 'coop',
    editionTag: 'C3',
    sourceIds: ['mists-official'],
    keywords: ['tura', 'kolejnosc'],
    level: 'basic',
    hubGroup: 'Mgła — kooperacja',
    content: [
      {
        type: 'resolutionOrder',
        title: 'Kolejność akcji',
        steps: [
          '1a. Dobierz dokładnie 1 kafelek i dołóż go bokiem do istniejącego (krawędzie drogi/miasta/pola muszą pasować; mgłę ignoruj przy dopasowaniu).',
          '1b. Sprawdź, czy zamknięto obszar mgły — jeśli tak, zdejmij wszystkie duchy z tego obszaru do zapasu.',
          '2a. Jeśli kafelek ma mgłę i obszar mgły nie jest zamknięty: wystaw duchy (patrz sekcja Mgła i duchy).',
          '2b. Opcjonalnie wystaw dokładnie 1 meepla ze swojego zapasu na właśnie dołożony kafelek (droga lub miasto, także pod mgłą; nigdy pole).',
          '3. Rozpatrz wszystkie punktowania wywołane tym kafelkiem: punkty ALBO usunięcie duchów.',
        ],
      },
      {
        type: 'exception',
        text: 'Nie wolno odmówić dobrania i dołożenia kafelka. Kafelek musi stykać się pełnym bokiem, nie samym narożnikiem.',
      },
      {
        type: 'paragraph',
        text: 'Jeśli dobrany kafelek nie ma żadnego legalnego miejsca, odłóż go na bok i dobierz kolejny (kontynuuj, aż znajdziesz legalny). Gdy stos się skończy przed osiągnięciem celu — przegrana.',
      },
    ],
  },
  {
    id: 'mists-roads-cities',
    title: 'Mgła — Drogi i miasta',
    requiredModuleIds: ['mists-over-carcassonne'],
    requiredMistsPlayMode: 'coop',
    editionTag: 'C3',
    sourceIds: ['mists-official'],
    keywords: ['droga', 'miasto', 'punktowanie'],
    level: 'basic',
    hubGroup: 'Mgła — kooperacja',
    content: [
      {
        type: 'heading',
        text: 'Drogi',
        level: 3,
      },
      {
        type: 'bulletList',
        items: [
          'Dołóż kafelek tak, by drogi łączyły się z drogami.',
          'Meepla na drogę wolno postawić tylko gdy ta droga jest wolna (bez meepla).',
          'Ukończona droga (oba końce zamknięte wsią, miastem, zamkiem lub pętlą): 1 punkt za każdy kafelek drogi.',
          'Po punktowaniu meeple wraca do zapasu właściciela.',
        ],
      },
      {
        type: 'heading',
        text: 'Miasta',
        level: 3,
      },
      {
        type: 'bulletList',
        items: [
          'Dołóż kafelek łącząc segmenty miasta.',
          'Meepla (rycerza) wolno postawić tylko na wolne miasto.',
          'Ukończone miasto (otoczone murami): 2 punkty za kafelek + 2 punkty za każdy herb.',
          'Po punktowaniu meeple wraca do zapasu.',
        ],
      },
      {
        type: 'heading',
        text: 'Wspólne punktowanie (joint scoring)',
        level: 3,
      },
      {
        type: 'paragraph',
        text: 'Jeśli na ukończonym obiekcie stoją meeple różnych kolorów, najpierw policz bazową wartość obiektu. Następnie za każdy dodatkowy kolor (poza pierwszym) dodajesz ponownie tę samą bazową wartość. Kilka meepli tego samego koloru nie mnoży dodatkowo.',
      },
      {
        type: 'example',
        title: 'Dwa kolory na drodze',
        text: 'Droga ma 4 kafelki (4 pkt). Stoją meeple żółty i niebieski. Liczycie 4 + 4 = 8 punktów łącznie na wspólny tor, potem oba meeple wracają do zapasów.',
      },
    ],
  },
  {
    id: 'mists-fog-ghosts',
    title: 'Mgła — Mgła i duchy',
    requiredModuleIds: ['mists-over-carcassonne'],
    requiredMistsPlayMode: 'coop',
    editionTag: 'C3',
    sourceIds: ['mists-official'],
    keywords: ['mgla', 'duch', 'obszar mgly'],
    level: 'intermediate',
    hubGroup: 'Mgła — kooperacja',
    content: [
      {
        type: 'paragraph',
        text: 'Mgła pokrywa pole lub drogę, nigdy miasto. Przy dokładaniu kafelka ignorujesz mgłę: mglista droga może stykać się z czystą drogą i odwrotnie.',
      },
      {
        type: 'heading',
        text: '1b. Zamknięcie obszaru mgły',
        level: 3,
      },
      {
        type: 'paragraph',
        text: 'Obszar mgły jest zamknięty, gdy nie da się go już powiększyć (jak miasto — otoczony ze wszystkich stron segmentów mgły). Jeśli choć jedna krawędź mgły styka się z polem lub drogą bez mgły w sposób uniemożliwiający domknięcie, taki obszar może być nie do zamknięcia.',
      },
      {
        type: 'paragraph',
        text: 'Po zamknięciu obszaru mgły zdejmij wszystkie duchy z powiązanych kafelków tego obszaru z powrotem do zapasu. Zamknięcie mgły samo w sobie nie daje punktów.',
      },
      {
        type: 'heading',
        text: '2a. Wystawianie duchów',
        level: 3,
      },
      {
        type: 'numberedList',
        items: [
          'Jeśli dołożony kafelek ma mgłę i obszar nie został właśnie zamknięty: wystaw na ten kafelek tyle duchów, ile symboli duchów jest na kafelku.',
          'Jeśli dołożeniem rozszerzyłeś istniejący obszar mgły (co najmniej jedna krawędź mgły styka się z mgłą już na planszy): wystaw o 1 ducha mniej.',
          'Jeśli po tej redukcji wynik wynosi 0 (np. jeden symbol i rozszerzenie), nie wystawiasz żadnego ducha na ten kafelek.',
        ],
      },
      {
        type: 'exception',
        text: 'Brak duchów w zapasie w chwili, gdy musisz wystawić co najmniej jednego — natychmiastowa przegrana.',
      },
      {
        type: 'heading',
        text: '2b. Meeple a mgła',
        level: 3,
      },
      {
        type: 'bulletList',
        items: [
          'Wolno stawiać meepla na drodze pokrytej mgłą.',
          'Nie wolno stawiać meepla na polu (łące), z mgłą ani bez.',
          'Na mieście zasady jak zwykle (bez mgły na mieście).',
        ],
      },
      {
        type: 'example',
        title: 'Rozszerzenie mgły',
        text: 'Kafelek ma 2 symbole duchów. Dokładasz go rozszerzając mgłę — wystawiasz 1 ducha. Późniejszy kafelek z 1 symbolem też rozszerza mgłę — wystawiasz 0 duchów.',
      },
    ],
  },
  {
    id: 'mists-scoring-choice',
    title: 'Mgła — Punktowanie: punkty albo usunięcie duchów',
    requiredModuleIds: ['mists-over-carcassonne'],
    requiredMistsPlayMode: 'coop',
    editionTag: 'C3',
    sourceIds: ['mists-official'],
    keywords: ['punktowanie', 'duchy', 'wybor'],
    level: 'intermediate',
    hubGroup: 'Mgła — kooperacja',
    content: [
      {
        type: 'paragraph',
        text: 'Gdy dołożenie kafelka wywołuje punktowanie ukończonej drogi, miasta lub (od poziomu 2) zamku, aktywny gracz dla każdego takiego punktowania osobno wybiera:',
      },
      {
        type: 'bulletList',
        items: [
          'A) Zdobyć punkty jak zwykle (przesuń wspólny znacznik; przy joint scoring dolicz kolory) i zdejmij punktujące meeple do zapasów, ALBO',
          'B) Zrezygnować z punktów tego punktowania i usunąć do 3 duchów z dokładnie jednego wybranego kafelka (także z cmentarza) z powrotem do zapasu.',
        ],
      },
      {
        type: 'exception',
        text: 'Jedno wspólne punktowanie obiektu z wieloma kolorami to jedno punktowanie — wybierasz punkty albo usunięcie duchów, nie oba.',
      },
      {
        type: 'exception',
        text: 'Kafelek startowy liczy się jak 4 kafelki przy punktowaniu obiektów, które go obejmują.',
      },
      {
        type: 'example',
        title: 'Usunięcie zamiast punktów',
        text: 'Ukończona droga dałaby 4 punkty, ale na sąsiednim kafelku stoją 3 duchy. Wybierasz opcję B i zdejmujesz te 3 duchy; znacznik punktacji się nie przesuwa za tę drogę.',
      },
    ],
  },
  {
    id: 'mists-end-l1',
    title: 'Mgła — Koniec gry i warunki (poziom 1)',
    requiredModuleIds: ['mists-over-carcassonne'],
    requiredMistsPlayMode: 'coop',
    editionTag: 'C3',
    sourceIds: ['mists-official'],
    keywords: ['zwyciestwo', 'przegrana'],
    level: 'basic',
    hubGroup: 'Mgła — kooperacja',
    content: [
      {
        type: 'heading',
        text: 'Zwycięstwo',
        level: 3,
      },
      {
        type: 'paragraph',
        text: 'Gdy wspólny znacznik punktacji dotrze lub przekroczy pole z żetonem celu (50 punktów na poziomie 1) — wygrywacie. Możecie przejść do kolejnego poziomu.',
      },
      {
        type: 'heading',
        text: 'Przegrana',
        level: 3,
      },
      {
        type: 'bulletList',
        items: [
          'Stos kafelków jest pusty, a cel nieosiągnięty.',
          'Musicie wystawić ducha, a w zapasie jest ich za mało.',
        ],
      },
      {
        type: 'note',
        text: 'Nie ma punktowania końcowego nieukończonych obiektów ani pól. Partia kończy się wyłącznie zwycięstwem lub przegraną jak wyżej.',
      },
    ],
  },
  {
    id: 'mists-level-2',
    title: 'Mgła — Poziom 2: Zamki i cmentarze',
    requiredModuleIds: ['mists-over-carcassonne'],
    requiredMistsPlayMode: 'coop',
    editionTag: 'C3',
    sourceIds: ['mists-official'],
    keywords: ['poziom 2', 'zamek', 'cmentarz'],
    level: 'intermediate',
    hubGroup: 'Mgła — kooperacja',
    content: [
      {
        type: 'paragraph',
        text: 'Do stosu wracają wszystkie 5 zamków i 5 cmentarzy. Cel: 75 punktów. Połóż żeton celu na polu 25; musicie dojść do niego dwa razy (szara strona w górę, po pierwszym dojściu odwróć na kolorową).',
      },
      {
        type: 'heading',
        text: 'Zamki',
        level: 3,
      },
      {
        type: 'resolutionOrder',
        title: 'Procedura zamku',
        steps: [
          'Dołóż kafelek z zamkiem zgodnie z dopasowaniem krawędzi (droga kończąca się w zamku łączy się jak droga).',
          'Możesz postawić meepla na zamku zamiast na drodze — tylko 1 meeple, zamek musi być wolny.',
          'Zamek punktuje, gdy jest całkowicie otoczony kafelkami (wszystkie 8 sąsiadów + sam zamek w otoczeniu).',
          'Punkty: 2 za każdy otaczający kafelek z mgłą, wliczając kafelek zamku, jeśli ma mgłę.',
          'Wybór jak zwykle: punkty albo usunięcie do 3 duchów z jednego kafelka.',
        ],
      },
      {
        type: 'heading',
        text: 'Cmentarze (poziomy 2–4)',
        level: 3,
      },
      {
        type: 'resolutionOrder',
        title: 'Procedura cmentarza',
        steps: [
          'Dołóż kafelek z cmentarzem normalnie.',
          'Nie wolno stawiać meepla na samym cmentarzu; wolno na drodze/mieście na tym kafelku.',
          'Gdy dołożone są kafelki do wszystkich 4 boków cmentarza (góra, dół, lewo, prawo): natychmiast pochować meepla.',
          'Pochowanie: weź meepla z planszy (lub z zapasu, jeśli nie masz żadnego na planszy) i połóż go leżącego na cmentarzu do końca gry.',
          'Zdejmij wszystkie duchy z kafelka cmentarza do zapasu.',
          'Jeśli pochowany meeple miał psa — usuń psa z gry.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Za każdym razem, gdy wystawiacie co najmniej 1 ducha na mgłę, musicie też dodać 1 ducha na jeden otwarty cmentarz (ma choć jeden wolny bok). Przy kilku otwartych — wybieracie drużynowo. Brak otwartego cmentarza = bez dodatkowego ducha.',
      },
      {
        type: 'example',
        title: 'Zamek z mgłą',
        text: 'Zamek otoczony; 5 sąsiadów ma mgłę + sam zamek z mgłą → 6 × 2 = 12 punktów (albo usunięcie duchów zamiast punktów).',
      },
    ],
  },
  {
    id: 'mists-level-3',
    title: 'Mgła — Poziom 3: Psy i stosy',
    requiredModuleIds: ['mists-over-carcassonne'],
    requiredMistsPlayMode: 'coop',
    editionTag: 'C3',
    sourceIds: ['mists-official'],
    keywords: ['poziom 3', 'pies', 'rufus', 'ronja'],
    level: 'advanced',
    hubGroup: 'Mgła — kooperacja',
    content: [
      {
        type: 'numberedList',
        items: [
          'Potasuj wszystkie 60 kafelków i podziel na 3 równe stosy po 20.',
          'Dobieracie tylko z aktywnego stosu; kolejny stos dopiero po wyczerpaniu poprzedniego.',
          'Rufus (pies 1) na polu 15 — musicie zdobyć co najmniej 15 pkt zanim skończy się pierwszy stos.',
          'Ronja (pies 2) na polu 0/50 — co najmniej 50 pkt zanim skończy się drugi stos.',
          'Żeton celu też na 0/50 — łącznie 100 punktów przed końcem trzeciego stosu.',
          'Niespełnienie któregokolwiek progu = przegrana poziomu.',
        ],
      },
      {
        type: 'heading',
        text: 'Aktywacja psa',
        level: 3,
      },
      {
        type: 'resolutionOrder',
        title: 'Gdy znacznik dotrze lub przekroczy pole z psem',
        steps: [
          'Natychmiast dołącz psa do dowolnego niepochowanego meepla na planszy.',
          'Usuń do 3 duchów z każdego z 2 różnych kafelków ortogonalnie lub diagonalnie sąsiadujących z kafelkiem tego meepla (w tym z jego własnego kafelka).',
          'Pozostałe kafelki z pierwszego stosu połóż na wierzch drugiego (więcej czasu na drugi próg).',
          'Analogicznie przy Ronji.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Pies zostaje przy meeplu do jego punktowania. Po punktowaniu meepla punktujesz psa: 1 punkt za każdego ducha obecnie na całej planszy. Nie wolno zamienić punktowania psa na usuwanie duchów. Potem pies wraca do pudełka.',
      },
    ],
  },
  {
    id: 'mists-level-4',
    title: 'Mgła — Poziom 4',
    requiredModuleIds: ['mists-over-carcassonne'],
    requiredMistsPlayMode: 'coop',
    editionTag: 'C3',
    sourceIds: ['mists-official'],
    keywords: ['poziom 4'],
    level: 'advanced',
    hubGroup: 'Mgła — kooperacja',
    content: [
      {
        type: 'paragraph',
        text: 'Jak poziom 3, ale: odłóż 2 duchy do pudełka (zostaje 13 w zapasie). Rufus na 20 (próg pierwszego stosu). Ronja na 10 (oznacza 60 łącznie po drugim). Cel na 20 (łącznie 120 punktów). Reguły zamków, cmentarzy (4 boki) i psów jak wyżej.',
      },
    ],
  },
  {
    id: 'mists-level-5',
    title: 'Mgła — Poziom 5: Nawiedzony cmentarz',
    requiredModuleIds: ['mists-over-carcassonne'],
    requiredMistsPlayMode: 'coop',
    editionTag: 'C3',
    sourceIds: ['mists-official'],
    keywords: ['poziom 5', 'nawiedzenie', 'cmentarz'],
    level: 'advanced',
    hubGroup: 'Mgła — kooperacja',
    content: [
      {
        type: 'bulletList',
        items: [
          '3 stosy po 20; odłóż 2 duchy (13 w grze).',
          'Rufus na 15, Ronja na 0/50, cel na 0/50 (100 punktów łącznie) — progi jak na karcie poziomu.',
        ],
      },
      {
        type: 'resolutionOrder',
        title: 'Nawiedzony cmentarz',
        steps: [
          'Dołóż cmentarz jak zwykle.',
          'Od razu dodaj 1 ducha na każdy sąsiedni kafelek z mgłą wokół cmentarza (niezależnie czy mgła zamknięta i czy już są duchy).',
          'Cmentarz zamyka się dopiero po otoczeniu 8 kafelkami (jak zamek), nie 4 bokami.',
          'Przy zamknięciu: pochować meepla; cmentarz jest zamknięty.',
          'Dopóki istnieje co najmniej 1 otwarty cmentarz: na końcu tury dobierz 1 kafelek bez patrzenia i połóż go zakryty przy otwartym cmentarzu.',
          'Nawet przy kilku otwartych cmentarzach dobierasz tylko 1 zakryty kafelek na turę (wybór cmentarza drużynowy).',
        ],
      },
      {
        type: 'warning',
        text: 'Zakryte kafelki nie kończą dróg, miast ani zamków — tylko pomagają zamykać cmentarze. Region sąsiadujący z zakrytym kafelkiem nie może już zostać ukończony. Zamek z zakrytym sąsiadem też nie da się ukończyć.',
      },
      {
        type: 'exception',
        text: 'Nowy odkryty kafelek może stykać się z zakrytym tylko jeśli jednocześnie styka się bokiem z innym już odkrytym kafelkiem. Zakryte wolno kłaść obok zakrytych.',
      },
    ],
  },
  {
    id: 'mists-level-6',
    title: 'Mgła — Poziom 6: Osobne punkty',
    requiredModuleIds: ['mists-over-carcassonne'],
    requiredMistsPlayMode: 'coop',
    editionTag: 'C3',
    sourceIds: ['mists-official'],
    keywords: ['poziom 6'],
    level: 'advanced',
    hubGroup: 'Mgła — kooperacja',
    content: [
      {
        type: 'numberedList',
        items: [
          'Zamiast jednego wspólnego znacznika: 3 meeple w 3 kolorach na polu 0/50 jako znaczniki punktacji (przy 3 graczach każdy kolor ma własny znacznik).',
          'W grze 2os./solo te 3 meeple bierzesz z pudełka, nie z osobistych zapasów.',
          'Odłóż 3 duchy (zostaje 12). 3 stosy. Psy na 10 i 25. Cel na 0/50.',
          'Cmentarze jak na poziomie 5 (nawiedzone).',
          'Każdy kolor punktuje osobno. Próg psa/celu uznaje się za osiągnięty dopiero gdy WSZYSTKIE znaczniki punktacji dotrą lub przekroczą to pole — dopiero wtedy aktywujecie psa.',
          'Gdy punktujesz kolor bez własnego znacznika na torze, wybierasz który ze znaczników przesunąć.',
          'Punktowanie meepla z psem przesuwa znacznik odpowiadającego koloru.',
        ],
      },
    ],
  },
  {
    id: 'mists-levels-summary',
    title: 'Mgła — Skrót wszystkich poziomów',
    requiredModuleIds: ['mists-over-carcassonne'],
    requiredMistsPlayMode: 'coop',
    editionTag: 'C3',
    sourceIds: ['mists-official'],
    keywords: ['poziom', 'scenariusz', 'skrot'],
    level: 'intermediate',
    hubGroup: 'Mgła — kooperacja',
    content: [
      {
        type: 'scoringTable',
        headers: ['Poziom', 'Kafelki specjalne', 'Duchy', 'Cel / progi'],
        rows: [
          ['1', 'Bez zamków i cmentarzy', '15', '50 pkt'],
          ['2', 'Zamki + cmentarze (4 boki)', '15', '75 (2× dojście do 25)'],
          ['3', 'Jak 2 + 3 stosy + psy', '15', '15 / 50 / 100'],
          ['4', 'Jak 3', '13', '20 / 60 / 120'],
          ['5', 'Nawiedzone cmentarze (8)', '13', '15 / 50 / 100'],
          ['6', 'Jak 5 + osobne tory', '12', '10 / 25 / 50 (wszyscy)'],
        ],
      },
      {
        type: 'resolutionOrder',
        title: 'Pełna kolejność (zależnie od poziomu)',
        steps: [
          '1a Dołóż kafelek (tylko z aktywnego stosu, jeśli dotyczy)',
          '1b Zamknij mgłę → zdejmij duchy',
          '1c Zamknij cmentarz → pochować meepla, zdejmij duchy z cmentarza',
          '2a Wystaw duchy (+ dodatkowy na otwarty cmentarz od poz. 2; wokół nawiedzonego cmentarza na poz. 5–6)',
          '2b Opcjonalnie meeple',
          '3a Punktuj (punkty lub −3 duchy z 1 kafelka)',
          '3b Psy (od poz. 3)',
          '3c Zakryty kafelek przy otwartym nawiedzonym cmentarzu (poz. 5–6)',
        ],
      },
    ],
  },

  // ——— Mode B: classic expansion ———
  {
    id: 'mists-classic-overview',
    title: 'Duchy, Zamki i Cmentarze — dodatek do klasycznego Carcassonne',
    requiredModuleIds: ['mists-over-carcassonne'],
    requiredMistsPlayMode: 'classic-expansion',
    editionTag: 'C3',
    sourceIds: ['mists-official', 'mists-wica'],
    keywords: ['duchy', 'zamek', 'cmentarz', 'ekspansja', 'strażnik'],
    level: 'advanced',
    hubGroup: 'Mgła — dodatek klasyczny',
    content: [
      {
        type: 'editionWarning',
        text: 'To osobny wariant konkurencyjny (Exp. 11). Nie mieszaj procedur z trybem kooperacyjnym poziomów 1–6.',
        oldEditionName: 'tryb kooperacyjny Mgły',
      },
      {
        type: 'paragraph',
        text: 'Cel i rytm partii jak w podstawie C3: każdy gra na własne punkty. Elementy Mgły służą do dokuczania rywalom duchami, a nie do wspólnego toru ani warunków przegranej kooperacji. Żetonu celu, karty poziomów i psów nie używajcie.',
      },
      {
        type: 'heading',
        text: 'Przygotowanie',
        level: 2,
      },
      {
        type: 'bulletList',
        items: [
          'Połóż duży kafelek startowy Mgły (2×2); klasyczny start odłóż do pudełka.',
          'Wtasuj 60 kafelków Mgły do stosu z podstawą (i innymi włączonymi dodatkami).',
          'Każdy gracz: 5 zwykłych meepli + 2 meeple-strażników (guard); 6. meeple na torze 0/50.',
          'Pozostałe strażniki kolorów graczy połóż przy torze jako zapas ogólny; 15 duchów obok.',
        ],
      },
      {
        type: 'note',
        text: 'Kafelek startowy 2×2 liczy się jak 4 zwykłe kafelki przy klasztorze, zamku i cmentarzu.',
      },
      {
        type: 'heading',
        text: '1. Dołożenie kafelka',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Dobierz i dołóż kafelek według C3, ignorując mgłę przy dopasowaniu krawędzi — mgła unosi się nad drogą lub polem i nie jest typem terenu.',
      },
      {
        type: 'heading',
        text: '2a. Duchy (rozszerzanie i ograniczanie mgły)',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Symbole duchów na kafelku w tym trybie nie wystawiają duchów na kafelek. Duch trafia na meepla zależnie od tego, jak ułożysz mgłę względem już leżących kafelków:',
      },
      {
        type: 'bulletList',
        items: [
          'Rozszerzanie: kafelek z mgłą styka się co najmniej jedną mglistą krawędzią z już istniejącą mgłą → dodaj 1 ducha do meepla przeciwnika na planszy.',
          'Ograniczanie: co najmniej jedna mglista krawędź styka się z krawędzią bez mgły → dodaj 1 ducha do własnego meepla na planszy.',
          'Oba naraz: najpierw duch na meepla przeciwnika, potem na własnego.',
          'Mgliste krawędzie przy pustym polu nie wywołują duchów.',
          'Brak duchów w zapasie albo brak meepla, na którego można położyć ducha → pomiń ten efekt.',
          'Trzeci duch na meeplu: natychmiast zdejmij tego meepla i wszystkie duchy przy nim (wracają do zapasów).',
          'Nigdy nie kładź duchów na meeple-strażniki — są odporne.',
        ],
      },
      {
        type: 'heading',
        text: '2b. Wystawienie figury',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Po duchach możesz wystawić zwykłego meepla lub strażnika na właśnie dołożonym kafelku jak w C3 (także farmera). Strażnik działa jak zwykły meeple, ale nie przyjmuje duchów.',
      },
      {
        type: 'heading',
        text: '3. Punktowanie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Punktuj jak w podstawie. Każdy duch przy punktowanym meeplu odejmuje 2 punkty (wynik może być ujemny względem obiektu; znacznik na torze nie schodzi poniżej 0). Potem meeple i jego duchy wracają do zapasów.',
      },
      {
        type: 'heading',
        text: 'Cmentarze',
        level: 2,
      },
      {
        type: 'bulletList',
        items: [
          'Możesz postawić meepla lub strażnika na cmentarzu jako strażnika cmentarza (cemetery keeper).',
          'Cmentarz punktuje, gdy jest całkowicie otoczony kafelkami (jak klasztor / zamek Mgły — 8 sąsiadów).',
          'Przy scoringu: weź dodatkowego meepla-strażnika swojego koloru z zapasu przy torze (jeśli jeszcze jest) i dodaj do swojego zapasu; strażnik cmentarza wraca do Twojego zapasu.',
          'Duchy przy strażniku cmentarza w chwili scoringu: −2 punkty za każdego.',
          'Nieukończone cmentarze nie punktują na końcu gry.',
        ],
      },
      {
        type: 'heading',
        text: 'Zamki',
        level: 2,
      },
      {
        type: 'bulletList',
        items: [
          'Możesz postawić zwykłego meepla lub strażnika na zamku.',
          'Zamek punktuje po pełnym otoczeniu: 2 punkty za każdy sąsiedni kafelek z mgłą (wliczając sam kafelek zamku).',
          'Na końcu gry nieukończony zamek z meeplem: 1 punkt za każdy sąsiedni kafelek z mgłą (w tym zamek).',
        ],
      },
      {
        type: 'heading',
        text: 'Pola i mgła',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Farmerzy są dozwoleni. Mgła rozdziela pola jak miasto. Na końcu gry każdy duch przy Twoich meeplach daje −1 punkt (osobno od −2 w trakcie gry).',
      },
      {
        type: 'commonMistake',
        text: 'Wystawianie duchów według symboli jak w kooperacji albo stosowanie wspólnego toru / zakazu farmerów z poziomów 1–6.',
      },
    ],
  },
];

export const mistsSections = upgradeMistsChapters(mistsSectionsRaw);
